import { TURKIYE_ENABLED } from "../config/features";

/** Every locale the app ships translations for, whether or not it is live. */
const ALL_LOCALES = ["en", "tr"] as const;

export type Locale = (typeof ALL_LOCALES)[number];

/**
 * The locales a user can actually reach. Turkish sits behind the Türkiye gate,
 * so with the flag off this is English only, and that single change closes
 * every route into the Turkish UI: browser-language detection stops matching
 * it, a /tr/ URL is no longer recognised as a locale prefix, and the Settings
 * language picker (which renders straight off this list) collapses to one
 * entry. i18n/index.ts feeds `supportedLngs` from here for the same reason.
 */
export const SUPPORTED_LOCALES: readonly Locale[] = TURKIYE_ENABLED
  ? ALL_LOCALES
  : (["en"] as const);

function isLocale(value: string | undefined | null): value is Locale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/** Reads the first path segment of the current URL, if it's a supported locale. */
export function getPathLocale(): Locale | null {
  const segment = window.location.pathname.split("/")[1];
  return isLocale(segment) ? segment : null;
}

/** localStorage → browser language → 'en' fallback. Used when the URL has no locale yet. */
export function detectPreferredLocale(): Locale {
  const stored = window.localStorage.getItem("helixa_language");
  if (isLocale(stored)) return stored;

  const navLang = window.navigator.language?.slice(0, 2);
  if (isLocale(navLang)) return navLang;

  return "en";
}

/**
 * Ensures the current URL starts with a supported locale segment.
 * If it doesn't, redirects to the same path under the preferred locale.
 * Also recovers legacy hash-based bookmarks (e.g. "#/folder/abc123" from the
 * old hash router) by carrying the hash path over as the redirect target.
 */
export function ensureLocalePrefix(): { locale: Locale; redirected: boolean } {
  const existing = getPathLocale();
  if (existing) return { locale: existing, redirected: false };

  const preferred = detectPreferredLocale();

  const hash = window.location.hash;
  const legacyPath = hash.startsWith("#/") ? hash.slice(1) : null;
  const restOfPath =
    legacyPath ?? (window.location.pathname === "/" ? "" : window.location.pathname);

  window.location.replace(`/${preferred}${restOfPath}${window.location.search}`);
  return { locale: preferred, redirected: true };
}
